import React from 'react'
import {
  useSection,
  Section,
  SectionHeader,
  SectionTitleSuffix,
  SectionContent,
} from 'components/Section'
import { observer } from 'mobx-react'
import { TableRow, TableHead, TableHeader, TableBody } from 'components/Table'
import ScopeChangeRequestDetailsViewModel from '../../ScopeChangeRequestDetailsViewModel'
import cx from 'classnames'
import CostPerCustomServiceViewModel from '../../CostPerCustomServiceViewModel'
import styles from './CustomItemsSection.sass'
import QuantityCell from '../components/QuantityCell'
import ServiceCell from '../components/ServiceCell'
import MoneyCell from '../components/MoneyCell'
import ServiceTableHeader from '../components/ServiceTableHeader'
import Button from 'components/Button'
import { ScopeChangeRequestBreakdownTable } from '../components/ScopeChangeRequestBreakdownTable'
import { ScopeChangePriceBreakDownItemForAdmin } from 'admin/scope-change-requests/ScopeChangeRequest'

interface CustomItemRowProps {
  vm: CostPerCustomServiceViewModel | ScopeChangePriceBreakDownItemForAdmin
  editable: boolean
}

const CustomItemRow = observer(function CustomItemRow({
  vm,
  editable,
}: CustomItemRowProps) {
  if (vm instanceof ScopeChangePriceBreakDownItemForAdmin) {
    return (
      <TableRow>
        <QuantityCell>{vm.quantityDiff}</QuantityCell>
        <ServiceCell>{vm.name}</ServiceCell>
        <MoneyCell
          editable={editable}
          model={vm}
          dataTest="scope-change-request-custom-item-price"
          field="priceDiff"
        >
          {vm.priceDiff}
        </MoneyCell>
        <MoneyCell
          editable={editable}
          model={vm}
          dataTest="scope-change-request-custom-item-pro-price"
          field="proPriceDiff"
        >
          {vm.proPriceDiff}
        </MoneyCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <QuantityCell>{vm.fromItem.quantity}</QuantityCell>
      <ServiceCell>{vm.fromItem.name}</ServiceCell>
      <MoneyCell
        editable={editable}
        model={vm}
        dataTest="scope-change-request-item-price"
        field="newPrice"
        error={vm.validation.getError('newPrice')}
      >
        {vm.fromItem.price}
      </MoneyCell>
      <MoneyCell
        editable={editable}
        model={vm}
        dataTest="scope-change-request-item-pro-price"
        field="newProPrice"
        error={vm.validation.getError('newProPrice')}
      >
        {vm.fromItem.proPrice}
      </MoneyCell>
    </TableRow>
  )
})

interface CustomItemsSectionProps {
  vm: ScopeChangeRequestDetailsViewModel
}

export default observer(function CustomItemsSection({
  vm,
}: CustomItemsSectionProps) {
  const sectionProps = useSection(false)
  const customItems = vm.scopeChangeRequest.isAccepted
    ? vm.scopeChangeRequestPriceBreakDown.customScopeChangeItems
    : vm.costPerCustomServiceItems

  return (
    <Section
      {...sectionProps}
      collapsed={!vm.customItemsExpanded}
      onToggleCollapse={vm.toggleCustomItemsSection}
    >
      <SectionHeader borderless>
        <div className={styles.headerContainer}>
          <div>
            Custom services{' '}
            <SectionTitleSuffix>
              (
              {
                vm.scopeChangeRequestPriceBreakDown.customScopeChangeItems
                  .length
              }
              )
            </SectionTitleSuffix>
          </div>
          {(vm.scopeChangeRequest.isPendingFinalization ||
            vm.scopeChangeRequest.isPendingAdminApproval) && (
            <div className={styles.editButton}>
              <Button
                type="text"
                onClick={() =>
                  vm.scopeChangeEditCustomItemsDialogVM.open(
                    vm.scopeChangeRequest,
                  )
                }
              >
                Edit
              </Button>
            </div>
          )}
        </div>
      </SectionHeader>

      <SectionContent>
        <ScopeChangeRequestBreakdownTable>
          <TableHead className={cx(styles.tableHead)}>
            <TableHeader
              className={cx(styles.quantityTableHeader)}
            ></TableHeader>
            <ServiceTableHeader align="left">Service</ServiceTableHeader>
            <ServiceTableHeader align="right">Client fee</ServiceTableHeader>
            <ServiceTableHeader align="right">Pro cut</ServiceTableHeader>
          </TableHead>
          <TableBody>
            {customItems.map((item, i) => (
              <CustomItemRow
                editable={
                  vm.scopeChangeRequest.isPendingFinalization ||
                  vm.scopeChangeRequest.isPendingAdminApproval
                }
                vm={item}
                key={i}
              />
            ))}
          </TableBody>
        </ScopeChangeRequestBreakdownTable>
      </SectionContent>
    </Section>
  )
})
